import React, { useState, useContext } from 'react';
import { PanoramaVFTConfig, PanoramaVFTInfospotConfig } from '../models/PanoramaVFTConfig';
import ToggleButton from '../components/ToggleButton';
import Panorama from '../views/Panorama';
import PanoramaVFTEditor from '../editors/PanoramaVFTEditor';
import StickyFooter from '../components/StickyFooter';
import { SettingsContext } from './Main';
import { useToast } from '../components/ToastContext';

type PanoramaVFTProps = {
    globalConfig: PanoramaVFTConfig,
    inEditorMode: boolean
};

function PanoramaVFT({ globalConfig, inEditorMode }: PanoramaVFTProps) {

    const [config, setConfig] = useState<PanoramaVFTConfig>(globalConfig);
    const [inEditMode, setInEditMode] = useState(false);
    const [canvasContext, setCanvasContext] = useState<any>(null);
    const [canvasLoaded, setCanvasLoaded] = useState(false);

    const { setIsToastOpen, setToastMessage, setToastTitle } = useToast();
    const { SAVE_URL } = useContext(SettingsContext);

    const updateConfig = (newConfig: PanoramaVFTConfig) => {
        setConfig(newConfig);
    }

    const onAddPanoramaInfospot = (x: number, y: number, z: number, panorama?: string): PanoramaVFTInfospotConfig => {
        var newInfospot = {
            eventName: "Hotspot",
            eventType: "hotspot",
            scene: panorama ?? (config.scenes.length ? config.scenes[0].eventName : ""),
            sourceAsset: "./images/uns.png",
            selectedAsset: "./images/sel.png",
            visitedAsset: "./images/vis.png",
            infoText: "Tooltip",
            infoTextDelta: 50,
            x: x,
            y: y,
            z: z,
            target: ""
        };

        config.infoSpots.push(newInfospot);
        updateConfig({
            ...config,
            infoSpots: config.infoSpots
        });

        return newInfospot;
    }

    const updateCanvasContext = (ctx: any) => {
        setCanvasContext(ctx);
        setCanvasLoaded(true);
    }

    const onEditPreviewToggle = () => {
        if (canvasLoaded) {
            setInEditMode(!inEditMode);
        } else {
            setToastTitle("Error");
            setToastMessage("Edit is only available after Canvas has been loaded.");
            setIsToastOpen(true);
        }
    }

    const onSave = async () => {
        try {
            const response = await fetch(SAVE_URL, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify(config)
            });
    
            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Slide saved.");
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
            setIsToastOpen(true);
        } catch {
            setToastTitle("Error");
            setToastMessage("Unknown error. Please try again.");
            setIsToastOpen(true);
        }
    }

    return (
        <div className='vft-frame h-full'>
            {inEditorMode ?
                <>
                    <div className="fixed bg-white top-0 w-full py-2 z-20">
                        <ToggleButton 
                            checkedLabel="Edit" 
                            uncheckedLabel="Preview" 
                            isChecked={inEditMode} 
                            onToggle={onEditPreviewToggle}/>
                    </div>
                    {inEditMode ? (
                        <div className='vft-edit-view'>
                            <h1>{config.title}</h1>
                            <PanoramaVFTEditor globalConfig={config} updateConfig={updateConfig} canvasContext={canvasContext} />
                            <StickyFooter onSlideSave={onSave} />
                        </div>
                    ) : (
                        <div className='vft-learn-view h-full'>
                            <Panorama inEditMode={inEditorMode}
                                globalConfig={config}
                                updateCanvasContext={updateCanvasContext}
                                addInfospot={onAddPanoramaInfospot}
                                 />
                        </div>
                    )}
                </>
                : <div className='vft-learn-view h-full'>
                    <Panorama inEditMode={false}
                        globalConfig={config}
                        updateCanvasContext={updateCanvasContext} 
                        addInfospot={onAddPanoramaInfospot} />
                </div>
            }
        </div>
    );
}

export default PanoramaVFT;